import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from '@mui/styles';
// import axios from 'axios';
import axios from "../utils/axiosConfig";
import { WEB_URL } from "../utils/constants";
import LoaderProcessing from "../components/LoaderProcessing";
import ahGreenDotLogo from "../images/assesshub_logo_green_dot.svg";
import SupportBot from "./SupportBot";
import { CircularProgress } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href={WEB_URL}>
        AssessHub
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
  },
  image: {
    backgroundImage: "url('./../../v2/images/login-page.svg')",
    backgroundRepeat: "no-repeat",
    backgroundColor: theme?.palette?.mode === "dark" ? theme?.palette?.grey?.[900] : theme?.palette?.grey?.[50] || '#fafafa',
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  paper: {
    padding: theme?.spacing?.(8, 4) || '64px 32px',
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100%",
    boxSizing: "border-box",
    // Mobile responsive padding
    '@media (max-width: 600px)': {
      padding: '40px 20px',
    },
    '@media (max-width: 400px)': {
      padding: '24px 16px',
    },
  },
  avatar: {
    margin: theme?.spacing?.(1) || '8px',
    backgroundColor: theme?.palette?.secondary?.main || '#dc004e',
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    maxWidth: "400px",
    marginTop: theme?.spacing?.(3) || '24px',
    padding: '0 8px',
    boxSizing: 'border-box',
  },
  submit: {
    margin: theme?.spacing?.(3, 0, 2) || '24px 0 16px',
    minHeight: '48px', // Touch-friendly button height
    fontSize: '1rem',
  },
  logo: {
    maxWidth: "100%",
    width: "450px",
    maxHeight: "70px",
    objectFit: "contain",
    '@media (max-width: 400px)': {
      width: '280px',
      maxHeight: '50px',
    },
  },
  successIcon: {
    color: "#4caf50",
  },
  errorIcon: {
    color: "#f44336",
  },
}));

export default function ResetPassword() {
  const classes = useStyles();
  let [newPassword, setNewPassword] = useState("");
  let [confirmPassword, setConfirmPassword] = useState("");
  let [success, setSuccess] = useState(false)
  let [errorMsg, setError] = useState("");
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [loading, setLoading] = React.useState(false);
  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)

  // Get token and email from URL
  let routeURL = new URLSearchParams(window.location.search);
  const token = routeURL.get('user-token')
  const email = routeURL.get('email')

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setError('Invalid or missing reset link. Please request a new password reset.')
        setValidating(false)
        return
      }

      try {
        const response = await axios
          .post("/reset-password/validate", {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Origin: "*",
              "Access-Control-Allow-Headers": "*",
              "Access-Control-Allow-Origin": "*",
            },
            email: email.toLowerCase(),
            token: token,
          })

        if (response.data.success) {
          if (response.data.valid) {
            setTokenValid(true)
          } else {
            setError('This reset link has expired or is invalid. Please request a new password reset.')
          }
        } else {
          setError(response.data.error || 'Something went wrong. Failed to validate link.')
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired reset link. Please request a new password reset.')
      } finally {
        setValidating(false)
      }
    }

    validateToken()
  }, [token, email])

  const handleSubmit = async (event) => {
    event.preventDefault();

    if(newPassword === confirmPassword){
      let flag = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/.test(newPassword);
      if(!flag){
        setError('*New Password must be minimum twelve characters, at least one uppercase letter, one lowercase letter, one number and one special character');
      } else {
        setLoading(true);
        setError("");

        axios
          .post("/reset-password", {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Origin: "*",
              "Access-Control-Allow-Headers": "*",
              "Access-Control-Allow-Origin": "*",
            },
            email: email.toLowerCase(),
            token: token,
            newPassword: newPassword,
          })
          .then((response) => {
            setLoading(false);

            if (response.data.success) {
              setSuccess(true)
            } else {
              setError(response.data.error || "Something went wrong.");
            }
          })
          .catch((error) => {
            setLoading(false);
            //, error.response.data
            setError(error.response.data.error);
          });
      }
    } else {
      setError('*New Password and Confirm New Password do not match');
    }
  };

  return (
    <>
      {" "}
      <Grid container component="main" id="main-body-two" className={classes.root}>
        <CssBaseline />
        <Grid item xs={false} sm={4} md={7} className={classes.image} />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <div className={classes.paper}>
            <img className={`img-responsive center-block ${classes.logo}`} src={ahGreenDotLogo} alt="React Logo" />
            {validating ? (
              <div style={{ textAlign: "center" }}>
                <CircularProgress size={64} sx={{ mb: 3, mt: 3, color: "#6f2981" }} />
                <div style={{ marginTop: "20px", paddingLeft: "20px" }}>
                    Validating Link. Please wait...
                </div>
              </div>
            ) : success ? (
              <div style={{ textAlign: "center" }}>
                <CheckCircleIcon style={{ fontSize: "80px", marginTop: "20px" }} className={`${classes.successIcon}`} />
                <div style={{ marginTop: "20px", paddingLeft: "20px", color: "#4caf50" }}>
                  Password has been reset successfully!
                </div>
                <Link href={process.env.REACT_APP_NAME == "v2-hdfc" ? "/v2/user/signin" : "/v2/user/login"} style={{ textDecoration: "none" }}>
                  <div style={{ width: "100%", textAlign: "center", marginTop: "20px", color: "rgb(123,96,176)", cursor: "pointer" }}>
                    Go to Login Page
                  </div>
                </Link>
              </div>
            ) : !tokenValid ? (
              <div style={{ textAlign: "center" }}>
                <ErrorIcon style={{ fontSize: "80px", marginTop: "20px" }} className={`${classes.errorIcon}`} />
                <Typography style={{ color: "red", margin: "20px 0" }}>{errorMsg}</Typography>
                <Link href="/v2/user/forgot-password" style={{ textDecoration: "none" }}>
                  <Button fullWidth variant="contained" color="primary" className={classes.submit}>
                      Request New Reset Link
                  </Button>
                </Link>
                <Link href={process.env.REACT_APP_NAME == "v2-hdfc" ? "/v2/user/signin" : "/v2/user/login"} style={{ textDecoration: "none" }}>
                  <div style={{ width: "100%", textAlign: "center", marginTop: "20px", color: "rgb(123,96,176)", cursor: "pointer" }}>
                    Go to Login Page
                  </div>
                </Link>
              </div>
            ) : (
              <form className={classes.form} onSubmit={handleSubmit} noValidate>
                <div>
                  Enter your new password below.
                </div>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="New Password"
                  type="password"
                  id="password"
                  // autoComplete="current-password"
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="confirmpassword"
                  label="Confirm New Password"
                  type="password"
                  id="confirmpassword"
                  // autoComplete="current-password"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
                <Typography style={{ color: "red" }}>{errorMsg}</Typography>

                {loading ? (
                  <div className="loaderProcess" style={{ margin: "24px 0px 16px" }}>
                    <LoaderProcessing />
                  </div>
                ) : (
                  <Button type="submit" fullWidth variant="contained" color="primary" className={classes.submit}>
                    Reset Password
                  </Button>
                )}
                <Link href={process.env.REACT_APP_NAME == "v2-hdfc" ? "/v2/user/signin" : "/v2/user/login"} style={{ textDecoration: "none" }}>
                  <div style={{ width: "100%", textAlign: "center", marginTop: "20px", color: "rgb(123,96,176)", cursor: "pointer" }}>
                    Go to Login Page
                  </div>
                </Link>
                <Box mt={5}>
                  <Copyright />
                </Box>
              </form>
            )}
          </div>
        </Grid>
      </Grid>
      <SupportBot />
    </>
  );
}
